import React from "react";
import styled, { css } from "styled-components";
import { BillingItemizedSummary } from "../Billing";
import { ButtonTabs } from "../ButtonTabs";
import { useEvent } from "../hooks/useEvent";
import {
  ItemizedSummary,
  ItemizedSummaryLineItem,
  ItemizedSummaryNote,
  ItemizedSummaryNoteItem,
  ItemizedSummaryTotals,
} from "../ItemizedSummary";
import { SpinnerButton } from "../SpinnerButton";
import { style } from "../style";
import { BillingLineItem } from "./types";

const shouldBeFixedMediaQuery = style.mediaQueries.largeMax;
const shouldNotBeFixedMediaQuery = style.mediaQueries.largeMin;
const isHalfScreen = style.mediaQueries.mediumMin;

const Container = styled.div<{ $expanded: boolean; $fixedMaxHeight: string }>`
  all: unset;
  width: 100%;

  display: flex;
  flex-direction: column;

  box-sizing: border-box;
  background: ${style.colors.white};

  ${shouldBeFixedMediaQuery} {
    position: fixed;
    bottom: 0;
    left: 0;
    border-top: ${(props) => (props.$expanded ? "none" : `1px solid ${style.colors.grey.primary}`)};
    z-index: ${style.vars.zIndex.top};

    max-height: min(${(props) => props.$fixedMaxHeight}, calc(100vh - 100px));

    ${isHalfScreen} {
      ${
        /** HACK! 215px is a the width of the sidebar in Seashell. We should make this a prop if we change the design of the create pages. */ ""
      }
      width: calc(100% - 215px);
      left: 215px;
    }
  }

  ${shouldNotBeFixedMediaQuery} {
    max-height: inherit;

    border-radius: ${style.vars.borderRadius.base};
    border: 1px solid ${style.colors.grey.primary};
  }
`;

const Mask = styled.div`
  background-color: ${style.colors.grey.lightest};
  opacity: 0.9;
  height: 100vh;
  width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  z-index: ${style.vars.zIndex.top};
`;

const wrapperCSS = css`
  all: unset;
  display: block;
  width: 100%;
  box-sizing: border-box;

  padding: ${style.vars.space[4]};
  padding-top: 0;
`;

const SummaryWrapper = styled.div`
  ${wrapperCSS}

  font-weight: ${style.vars.fontWeight.bold};
  font-size: ${style.vars.fontSize.base};
`;

const WrapperDivider = styled.div<{ $flex?: boolean }>`
  ${wrapperCSS}

  ${(props) => {
    if (props.$flex) {
      return {
        display: "flex",
        gap: style.vars.space[2],

        /**
         * This reverses the order of the summary and the buttons on mobile so that
         * the "primary" (first) button always appears on the right.
         */
        flexDirection: "row-reverse",
      };
    }
  }}
`;

const Divider = styled.div`
  padding-top: ${style.vars.space[4]};
`;

type ExpandableProps = {
  expandable: true;
  isFixed: true;
  expanded: boolean;
  onToggleExpanded(): void;
};

type NonExpandableProps = {
  expandable: false;
  isFixed: false;
  expanded?: never;
};

type ButtonState = {
  /**
   * The current state of the button.
   *
   * `"enabled"`: The button is enabled and can be clicked.
   *
   * `"disabled"`: Something is wrong with the form and the button cannot be clicked.
   *
   * `"submitting"`: The button is disabled and a spinner is shown.
   */
  type: "enabled" | "disabled" | "submitting";
  text: string;
  variation?: "primary" | "secondary" | "tertiary";
  onClick?(): void;
};

const useMobileExpandableProps = (): ExpandableProps | NonExpandableProps => {
  const [expanded, setExpanded] = React.useState(false);

  const isFixed = shouldBeFixedMediaQuery.useMatchesBreakpoint();

  const onToggleExpanded = useEvent(() => {
    setExpanded((prev) => !prev);
  });

  if (isFixed) {
    return {
      expandable: true,
      isFixed: true,
      expanded,
      onToggleExpanded,
    };
  } else {
    return {
      expandable: false,
      isFixed: false,
    };
  }
};

type SubmitButtonProps = {
  state: ButtonState;
};

const SubmitButton: React.FC<SubmitButtonProps> = (props) => {
  const { state } = props;

  const disabled = state.type !== "enabled";
  const spinning = state.type === "submitting";
  const variation = state.variation ?? "primary";

  const onClick = useEvent<React.MouseEventHandler>((ev) => {
    if (state.onClick) {
      ev.preventDefault();
      state.onClick();
    }
  });

  return (
    <SpinnerButton fullWidth inline type="submit" onClick={onClick} disabled={disabled} spinning={spinning} variation={variation}>
      {state.text}
    </SpinnerButton>
  );
};

const renderButtonStates = (states: ButtonState[], isFixed: boolean): React.ReactNode => {
  if (isFixed) {
    return (
      <WrapperDivider $flex>
        {states.map((state, index) => (
          <SubmitButton key={index} state={state} />
        ))}
      </WrapperDivider>
    );
  }

  return (
    <>
      {states.map((state, index) => (
        <WrapperDivider key={index}>
          <SubmitButton state={state} />
        </WrapperDivider>
      ))}
    </>
  );
};

type Tab = {
  title: React.ReactNode;
  value: string;
};

export type Tabs = {
  options: Tab[];
  selected?: string;
  onSelect(value: string): void;
};

type ItemizedSummaryProps = {
  /**
   * A list of line items to display to the user.
   */
  items: ItemizedSummaryLineItem[];

  /**
   * What to display in the totals section.
   */
  totals: ItemizedSummaryTotals;

  buttonState: ButtonState | ButtonState[];

  note?: ItemizedSummaryNoteItem | ItemizedSummaryNoteItem[];

  tabs?: Tabs;
};

export const CreatePageV2ItemizedSummary = (props: ItemizedSummaryProps): JSX.Element => {
  const { items, buttonState, totals, tabs, note } = props;
  const maxHeight = 650;

  const notes = note ? (Array.isArray(note) ? note : [note]) : [];
  const buttonStates = Array.isArray(buttonState) ? buttonState : [buttonState];

  const expandableProps = useMobileExpandableProps();

  let renderedTabs: React.ReactNode;

  if (tabs) {
    renderedTabs = (
      <WrapperDivider>
        <ButtonTabs compact={!expandableProps.isFixed} {...tabs} />
      </WrapperDivider>
    );
  }

  return (
    <>
      {expandableProps.isFixed && expandableProps.expanded && <Mask onClick={expandableProps.onToggleExpanded} />}
      <Container $expanded={expandableProps.expanded ?? false} $fixedMaxHeight={`${maxHeight}px`}>
        {!expandableProps.isFixed && (
          <>
            <Divider />
            <SummaryWrapper>Summary</SummaryWrapper>
          </>
        )}
        {!expandableProps.isFixed && renderedTabs}
        <ItemizedSummary items={items} totals={totals} UNSAFE_maxHeight={maxHeight} UNSAFE_borderTop {...expandableProps} />
        <Divider />
        {renderButtonStates(buttonStates, expandableProps.isFixed)}
        {expandableProps.isFixed && renderedTabs}
        {notes &&
          notes.map((note, index) => (
            <WrapperDivider key={index}>
              <ItemizedSummaryNote item={note} />
            </WrapperDivider>
          ))}
      </Container>
    </>
  );
};

CreatePageV2ItemizedSummary.displayName = "CreatePageV2ItemizedSummary";

type ResourceSummaryProps = {
  /**
   * A list of billing line items to display to the user. Forwarded to the billing component.
   */
  items: BillingLineItem[];

  /**
   * The unit to present the grand total in.
   *
   * If `"monthly"`, the grand total will be presented as a monthly cost with the hourly cost in smaller text below.
   * If `"hourly"`, the grand total will be presented as an hourly cost with the monthly cost in smaller text below.
   */
  primaryUnit?: "monthly" | "hourly";

  buttonState: ButtonState | ButtonState[];

  note?: ItemizedSummaryNoteItem | ItemizedSummaryNoteItem[];
};

export const CreatePageV2ResourceSummary: React.FC<ResourceSummaryProps> = (props) => {
  const { items, buttonState, note, primaryUnit = "monthly" } = props;
  const maxHeight = 650;

  const notes = note ? (Array.isArray(note) ? note : [note]) : [];
  const buttonStates = Array.isArray(buttonState) ? buttonState : [buttonState];

  const expandableProps = useMobileExpandableProps();

  return (
    <>
      {expandableProps.isFixed && expandableProps.expanded && <Mask onClick={expandableProps.onToggleExpanded} />}
      <Container $expanded={expandableProps.expanded ?? false} $fixedMaxHeight={`${maxHeight}px`}>
        {!expandableProps.isFixed && (
          <>
            <Divider />
            <SummaryWrapper>Summary</SummaryWrapper>
          </>
        )}
        <BillingItemizedSummary
          primaryUnit={primaryUnit}
          items={items}
          UNSAFE_listMaxHeight={maxHeight}
          UNSAFE_borderTop
          {...expandableProps}
        />
        <Divider />
        {renderButtonStates(buttonStates, expandableProps.isFixed)}
        {notes &&
          notes.map((note, index) => (
            <WrapperDivider key={index}>
              <ItemizedSummaryNote item={note} />
            </WrapperDivider>
          ))}
      </Container>
    </>
  );
};

CreatePageV2ResourceSummary.displayName = "CreatePageV2ResourceSummary";
